import React, { memo, useCallback, useEffect, useState } from 'react';
import { Field, useFormState } from 'react-final-form';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ThreeDots } from 'react-loader-spinner';

import useLanguagesOptions from '../../../../../hooks/useLanguagesOptions';
import WithLabel, { LABEL_STYLES } from '../../../../../components/with-label/WithLabel';
import DropdownWithIcons from '../../../../../components/common-dropdown/DropdownWithIcons';
import WithError from '../../../../../components/with-error/WithError';
import { Input } from '../../../../../components/common-inputs';
import ColorInput from '../../../../../components/color-input/ColorInput';
import CommonButton from '../../../../../components/common-button/CommonButton';
import CommonTextarea from '../../../../../components/common-textarea/CommonTextarea';
import useRedirectPrompt from '../../../../../hooks/useRedirectPrompt';
import UnsavedModal from '../../../../../components/unsaved-modal/UnsavedModal';

import scss from './WriterForm.module.scss';

const WriterForm = ({ handleSubmit, disableLanguage }) => {
  const { t } = useTranslation('settings');
  const { dirty, valid, submitting } = useFormState();
  const navigate = useNavigate();
  const languageOptions = useLanguagesOptions();

  const isLoading = useSelector((state) => state.scoutSettings.isWriterFormLoading);

  const [showPrompt, confirmNavigation, cancelNavigation] = useRedirectPrompt(dirty && !submitting);
  const [isShowUnsavedModal, setIsShowUnsavedModal] = useState(false);

  const handleCancelModal = useCallback(() => {
    setIsShowUnsavedModal(false);

    if (showPrompt) {
      cancelNavigation();
    }
  }, [showPrompt, cancelNavigation]);

  const handleDontSave = useCallback(() => {
    setIsShowUnsavedModal(false);

    if (showPrompt) {
      confirmNavigation();
    }
  }, [showPrompt, confirmNavigation]);

  const handleSaveModal = useCallback(() => {
    setIsShowUnsavedModal(false);
    handleSubmit();

    if (valid && showPrompt) {
      confirmNavigation();
    }

    if (!valid && showPrompt) {
      cancelNavigation();
    }
  }, [showPrompt, confirmNavigation, handleSubmit, valid, cancelNavigation]);

  const handleCancel = useCallback(() => {
    navigate('/settings/scout');
  }, [navigate]);

  useEffect(() => {
    setIsShowUnsavedModal(showPrompt);
  }, [showPrompt]);

  return (
    <>
      <form className={scss.form} onSubmit={handleSubmit}>
        <Field name='language'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.CUSTOM} label={t('language')} extraClassName={scss.customLabel} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <DropdownWithIcons
                  options={languageOptions}
                  value={input.value}
                  onSelect={input.onChange}
                  onBlur={input.onBlur}
                  onFocus={input.onFocus}
                  name={input.name}
                  tabIndex={'-6'}
                  placeholder={t('select')}
                  isError={meta.touched && meta.error}
                  disabled={isLoading}
                  isEmojiIcons
                  isReadOnly={disableLanguage}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='writerName'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.CUSTOM} label={t('writerName')} extraClassName={scss.customLabel} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <Input
                  type='text'
                  tabIndex={'-5'}
                  isError={meta.touched && meta.error}
                  formProps={input}
                  size='sm'
                  disabled={isLoading}
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <Field name='writerColor'>
          {({ meta, input }) => (
            <WithLabel variant={LABEL_STYLES.CUSTOM} label={t('writerColor')} extraClassName={scss.customLabel} isBold>
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <ColorInput
                  color={input.value}
                  size={'sm'}
                  onChange={input.onChange}
                  tabIndex={'-4'}
                  isError={meta.touched && meta.error}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  disabled={isLoading}
                  disableAlpha
                />
              </WithError>
            </WithLabel>
          )}
        </Field>

        <WithLabel
          variant={LABEL_STYLES.CUSTOM}
          label={t('writerPhrase')}
          extraClassName={scss.customLabelTextEditor}
          isBold>
          <Field name='writerPhrase'>
            {({ meta, input }) => (
              <WithError isShowError={meta.touched && meta.error} errorText={t(meta.error)} isAbsolute>
                <CommonTextarea
                  value={input.value}
                  tabIndex={'-3'}
                  onChange={input.onChange}
                  onFocus={input.onFocus}
                  onBlur={input.onBlur}
                  isError={meta.touched && meta.error}
                  rows={10}
                  disabled={isLoading}
                />
              </WithError>
            )}
          </Field>

          <div className={scss.actions}>
            <CommonButton
              styleColor='secondary'
              type='button'
              onClick={handleCancel}
              maxWidth={120}
              tabIndex={'-1'}
              disabled={isLoading}>
              {t('cancel')}
            </CommonButton>

            <CommonButton
              styleColor='primary'
              type='submit'
              onClick={handleSubmit}
              maxWidth={120}
              tabIndex={'-2'}
              disabled={isLoading}>
              {isLoading ? <ThreeDots height={15} width={40} /> : null}
              {t('save')}
            </CommonButton>
          </div>
        </WithLabel>
      </form>

      <UnsavedModal
        isOpen={isShowUnsavedModal}
        onCancel={handleCancelModal}
        onClose={handleCancelModal}
        onDontSave={handleDontSave}
        onSave={handleSaveModal}
      />
    </>
  );
};

export default memo(WriterForm);
